import React, { useState, useEffect } from 'react';
import { V } from '../utils/theme';
import { Icons } from '../components/Icons';
import { Btn, ConfirmCtrl, SuccessToastCtrl, Skeleton } from '../components/ui';
import { Notice, Callout } from '../components/kit';
import { ScopeToggles } from '../components/ConsentSheet';
import { Switch } from '../components/FullSheet';
import { applyBrand } from '../utils/brand';
import { useOrg, orgApi, orgErr, errorMessage, onOrgPing, CONSENT_DEFAULTS, personName, shortScope, sourceLabel, fmtDay } from '../utils/org';

// ─── Goals from your coach (Phase C / A7) ───
// Active goals for the member (`list_goals`), with an inline "Update" for the kinds the
// member tracks themselves (weight / habit / custom → `update_goal_progress`). Strength
// and sessions goals are measured by the coach from logged data.
const SELF_REPORTED = ['weight', 'habit', 'custom'];
const goalPct = (g) => {
  const t = parseFloat(g.target), c = parseFloat(g.current);
  if (!Number.isFinite(t) || t === 0 || !Number.isFinite(c)) return null;
  return Math.max(0, Math.min(100, Math.round((c / t) * 100)));
};
const fmtNum = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));

function GoalRow({ goal, first, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(goal.current ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const pct = goalPct(goal);
  const editable = SELF_REPORTED.includes(goal.kind);
  const save = async () => {
    const n = parseFloat(val);
    if (!Number.isFinite(n)) { setErr({ code: 'invalid_goal' }); return; }
    setBusy(true); setErr(null);
    try {
      const r = await orgApi('update_goal_progress', { goal_id: goal.id, current: n });
      onSaved(r?.goal || { ...goal, current: n });
      setEditing(false);
      SuccessToastCtrl.show('Progress updated');
    } catch (e) { setErr(orgErr(e)); }
    setBusy(false);
  };
  return (
    <div style={{ paddingTop: 10, paddingBottom: 10, paddingLeft: 12, paddingRight: 12, borderTop: first ? "none" : `1px solid ${V.divider}` }} data-testid="coach-goal">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: V.text }}>{goal.title}</div>
          <div style={{ fontSize: 11, color: V.text3, marginTop: 2, fontFamily: V.mono }}>
            {fmtNum(goal.current)}{goal.target != null ? ` / ${fmtNum(goal.target)}` : ''}{goal.unit ? ` ${goal.unit}` : ''}
            {goal.due ? <span style={{ fontFamily: V.font }}> · due {fmtDay(goal.due)}</span> : null}
          </div>
        </div>
        {editable && !editing && (
          <button type="button" onClick={() => { setVal(goal.current ?? ''); setEditing(true); }} aria-label={`Update ${goal.title}`}
            style={{ fontSize: 11, fontWeight: 700, color: V.accent, background: `${V.accent}14`, border: `1px solid ${V.accent}28`, borderRadius: 8, paddingTop: 5, paddingBottom: 5, paddingLeft: 10, paddingRight: 10, cursor: "pointer", fontFamily: V.font }}>Update</button>
        )}
      </div>
      {pct !== null && (
        <div style={{ height: 4, borderRadius: 2, background: V.divider, marginTop: 8, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: pct >= 100 ? V.accent : V.warn, transition: "width .3s" }} />
        </div>
      )}
      {editing && (
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8 }}>
          <input type="number" inputMode="decimal" value={val} onChange={e => setVal(e.target.value)} aria-label={`Current ${goal.unit || 'value'}`} disabled={busy}
            style={{ flex: 1, minWidth: 0, fontSize: 14, fontFamily: V.mono, color: V.text, background: V.inputBg, border: `1px solid ${V.cardBorder}`, borderRadius: 8, paddingTop: 7, paddingBottom: 7, paddingLeft: 10, paddingRight: 10 }} />
          {goal.unit && <span style={{ fontSize: 11, color: V.text3 }}>{goal.unit}</span>}
          <Btn v="small" disabled={busy} onClick={save}>Save</Btn>
          <Btn v="ghost" disabled={busy} onClick={() => { setEditing(false); setErr(null); }} s={{ minHeight: 36 }}>Cancel</Btn>
        </div>
      )}
      {err && <Notice tone="error" style={{ marginTop: 6 }}>{err.code === 'invalid_goal' ? 'Enter a number.' : errorMessage(err)}</Notice>}
    </div>
  );
}

export function CoachGoals() {
  const [goals, setGoals] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let dead = false;
    const load = () => orgApi('list_goals').then(r => { if (!dead) { setGoals(Array.isArray(r?.goals) ? r.goals.filter(g => g.status === 'active') : []); setErr(null); } })
      .catch(e => { if (!dead) { setGoals(g => g || []); setErr(orgErr(e)); } });
    load();
    const off = onOrgPing((p) => { if (p && p.kind === 'goal') load(); });
    return () => { dead = true; off(); };
  }, []);
  const onSaved = (g) => setGoals(gs => (gs || []).map(x => (x.id === g.id ? { ...x, ...g } : x)).filter(x => x.status === 'active'));
  return (
    <div data-testid="coach-goals">
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: V.text3, marginBottom: 8 }}>Goals from your coach</div>
      {goals === null ? <Skeleton rows={2} />
        : goals.length === 0 ? <div style={{ fontSize: 12, color: V.text3 }}>No goals yet — your coach can set targets for you here.</div>
        : <div style={{ borderRadius: 12, border: `1px solid ${V.cardBorder}`, overflow: "hidden" }}>
          {goals.map((g, i) => <GoalRow key={g.id} goal={g} first={i === 0} onSaved={onSaved} />)}
        </div>}
      {err && <Notice tone="error" style={{ marginTop: 6 }}>{errorMessage(err)}</Notice>}
    </div>
  );
}

// ─── Notifications (Phase G / B5) ───
// One member-side switch: push for gym messages (`push_messages`). The other pref keys
// (`push_checkins`, `daily_digest_*`) are coach-side and live in the portal. Loaded via
// get_notification_prefs; a failed load hides the row rather than showing a dead toggle.
export function NotificationPrefs() {
  const [prefs, setPrefs] = useState(null); // null loading · false unavailable · object
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let dead = false;
    orgApi('get_notification_prefs').then(r => { if (!dead) setPrefs(r?.prefs && typeof r.prefs === 'object' ? r.prefs : false); })
      .catch(() => { if (!dead) setPrefs(false); });
    return () => { dead = true; };
  }, []);
  if (prefs === false) return null;
  const on = prefs ? prefs.push_messages !== false : true;
  const toggle = async (v) => {
    const prev = prefs;
    setPrefs(p => ({ ...(p || {}), push_messages: v })); setBusy(true); setErr(null);
    try {
      const r = await orgApi('set_notification_prefs', { push_messages: v });
      if (r?.prefs) setPrefs(r.prefs);
    } catch (e) { setPrefs(prev); setErr(orgErr(e)); }
    setBusy(false);
  };
  return (
    <div data-testid="gym-notifications">
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: V.text3, marginBottom: 8 }}>Notifications</div>
      <div style={{ borderRadius: 12, border: `1px solid ${V.cardBorder}`, overflow: "hidden" }}>
        {prefs === null ? <div style={{ paddingTop: 10, paddingBottom: 10, paddingLeft: 12, paddingRight: 12 }}><Skeleton rows={1} /></div> : (
          <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 10, paddingBottom: 10, paddingLeft: 12, paddingRight: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: V.text }}>Push for gym messages</div>
              <div style={{ fontSize: 11, color: V.text3, marginTop: 2 }}>A notification when your coach or gym messages you.</div>
            </div>
            <Switch on={on} onChange={toggle} label="Push for gym messages" disabled={busy} />
          </div>
        )}
      </div>
      {err && <Notice tone="error" style={{ marginTop: 6 }}>{errorMessage(err)}</Notice>}
    </div>
  );
}

// Settings → "Your gym": org name, coach(es), the five sharing toggles
// (source 'settings'), Stop sharing everything, Leave, and the last 5 consent
// events. Rendered only when the account is in an org (active or suspended).
export function YourGymSection({ d }) {
  const { org, raw, suspended, refreshOrg } = useOrg();
  const me = org || raw;
  const orgName = me?.org?.name || 'your gym';
  const isStaff = me?.role === 'owner' || me?.role === 'coach';
  const [consent, setConsent] = useState(me?.consent ? { ...CONSENT_DEFAULTS, ...me.consent } : null);
  const [history, setHistory] = useState(null);
  const [coaches, setCoaches] = useState(me?.coaches?.length ? me.coaches : (me?.coach ? [me.coach] : []));
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let dead = false;
    if (isStaff || suspended) { setHistory([]); return; } // suspended: every org read/write 423s — don't call
    orgApi('get_consent').then(r => {
      if (dead) return;
      if (r?.consent) setConsent({ ...CONSENT_DEFAULTS, ...r.consent });
      if (r?.coaches?.length) setCoaches(r.coaches); else if (r?.coach) setCoaches([r.coach]);
      setHistory(r?.history || []);
    }).catch(e => { if (!dead) { setHistory([]); const er = orgErr(e); if (er.status !== 404) setErr(er); } });
    return () => { dead = true; };
  }, [me?.org?.id, isStaff, suspended]);

  const save = async (next) => {
    const prev = consent;
    setConsent(next); setErr(null); setBusy(true);
    const patch = {};
    for (const k of Object.keys(CONSENT_DEFAULTS)) if (!prev || prev[k] !== next[k]) patch[k] = next[k];
    try {
      const r = await orgApi('set_consent', { ...patch, source: 'settings' });
      if (r?.consent) setConsent({ ...CONSENT_DEFAULTS, ...r.consent });
      const r2 = await orgApi('get_consent').catch(() => null);
      if (r2?.history) setHistory(r2.history);
      refreshOrg();
    } catch (e) { setConsent(prev); setErr(orgErr(e)); }
    setBusy(false);
  };

  const revokeAll = () => ConfirmCtrl.show('Stop sharing everything?', `Your coach at ${orgName} will immediately lose access to your workouts, check-ins, nutrition, body data and photos. They still see your name, last active date and session count.`, async () => {
    setBusy(true); setErr(null);
    try {
      const r = await orgApi('revoke_all_consent');
      setConsent({ ...CONSENT_DEFAULTS, ...(r?.consent || {}), share_workouts: false, share_checkins: false });
      const r2 = await orgApi('get_consent').catch(() => null);
      if (r2?.history) setHistory(r2.history);
      SuccessToastCtrl.show('Sharing stopped');
      refreshOrg();
    } catch (e) { setErr(orgErr(e)); }
    setBusy(false);
  });

  const leave = () => ConfirmCtrl.show(`Leave ${orgName}?`, `The app goes back to the IronLog look, your coach loses access to your data, and gym messages stop. Everything you've logged stays on your account.`, async () => {
    setBusy(true); setErr(null);
    try {
      await orgApi('leave_org');
      applyBrand(null);
      await refreshOrg();
      SuccessToastCtrl.show(`You left ${orgName}`);
      d?.({ type: 'TAB', tab: 'settings' });
    } catch (e) { setErr(orgErr(e)); }
    setBusy(false);
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: V.text }}>{orgName}</div>
          <div style={{ fontSize: 11, color: V.text3, marginTop: 2 }}>
            {suspended ? 'Account paused — your data is safe' : me?.role === 'owner' ? 'You own this gym' : me?.role === 'coach' ? 'You coach here' : coaches.length ? `Coach: ${coaches.map(personName).join(', ')}` : 'No coach assigned yet'}
          </div>
        </div>
        {suspended && <span style={{ fontSize: 10, fontWeight: 700, color: V.warn, padding: "4px 8px", borderRadius: 8, background: `${V.warn}14`, border: `1px solid ${V.warn}30` }}>Paused</span>}
      </div>

      {!isStaff && suspended && (
        <Notice tone="warn">Sharing is paused while {orgName}'s account is paused. Your coach can't see your data, and your sharing choices are kept for when the account is active again.</Notice>
      )}
      {!isStaff && !suspended && (<>
        <CoachGoals />
        <Callout icon={Icons.users}>Your coach can always see: your name, when you last used the app, and a summary of how many sessions you log.</Callout>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: V.text3 }}>Share with your coach</div>
        {consent === null && history === null ? <Skeleton rows={4} />
          : <ScopeToggles value={consent || CONSENT_DEFAULTS} onChange={save} disabled={busy} />}
        {err && <Notice tone="error">{errorMessage(err, orgName)}</Notice>}
        <Btn v="danger" full disabled={busy || (consent && !Object.values(consent).some(Boolean))} onClick={revokeAll}>Stop sharing everything</Btn>

        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: V.text3, marginTop: 4 }}>Recent changes</div>
        {history === null ? <Skeleton rows={2} /> : history.length === 0
          ? <div style={{ fontSize: 12, color: V.text3 }}>No changes yet.</div>
          : <div style={{ borderRadius: 12, border: `1px solid ${V.cardBorder}`, overflow: "hidden" }}>
            {history.slice(0, 5).map((h, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 8, paddingBottom: 8, paddingLeft: 12, paddingRight: 12, borderTop: i ? `1px solid ${V.divider}` : "none" }}>
                <span aria-hidden style={{ width: 7, height: 7, borderRadius: 4, background: h.granted ? V.accent : V.danger, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: V.text, flex: 1, minWidth: 0 }}>
                  {h.granted ? 'Shared' : 'Stopped sharing'} {shortScope(h.scope)} · {sourceLabel(h.source)}
                </span>
                <span style={{ fontSize: 10, color: V.text3, fontFamily: V.mono, flexShrink: 0 }}>{fmtDay(h.created_at)}</span>
              </div>
            ))}
          </div>}
      </>)}

      {!suspended && <NotificationPrefs />}

      {me?.role !== 'owner' && (
        <Btn v="secondary" full disabled={busy} onClick={leave} s={{ color: V.danger, marginTop: 4 }}>Leave {orgName}</Btn>
      )}
      {me?.role === 'owner' && <div style={{ fontSize: 11, color: V.text3 }}>Owners manage the gym from the coach portal. Contact support to close the account.</div>}
      {isStaff && err && <Notice tone="error">{errorMessage(err, orgName)}</Notice>}
    </div>
  );
}
